import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Linkedin, Mail } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const handleHashLink = (path) => {
    if (path.includes('#')) {
      const [route, hash] = path.split('#');
      if (window.location.pathname === '/' || window.location.pathname === route) {
        // Already on the page, just scroll
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Navigate to page first, then scroll
        navigate(route || '/');
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    } else {
      navigate(path);
    }
  };

  const socialLinks = [
    {
      name: 'Facebook',
      url: 'https://www.facebook.com/profile.php?id=61584241436471',
      icon: Facebook,
      color: 'hover:text-blue-600'
    },
    {
      name: 'Instagram',
      url: 'https://www.instagram.com/routeflowai/',
      icon: Instagram,
      color: 'hover:text-pink-600'
    },
    {
      name: 'TikTok',
      url: 'https://www.tiktok.com/@routeflowai',
      icon: () => (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
        </svg>
      ),
      color: 'hover:text-gray-900'
    },
    {
      name: 'X (Twitter)',
      url: 'https://x.com/abbas224',
      icon: Twitter,
      color: 'hover:text-gray-900'
    },
    {
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/company/110326938/admin/dashboard/',
      icon: Linkedin,
      color: 'hover:text-blue-700'
    }
  ];

  const quickLinks = [
    { name: 'About Us', path: '/#features', isHash: true },
    { name: 'How It Works', path: '/#how-it-works', isHash: true },
    { name: 'Fees & Pricing', path: '/fees', isHash: false },
    { name: 'Help Center', path: '/help', isHash: false },
    { name: 'Contact Us', path: '/contact', isHash: false }
  ];

  const legalLinks = [
    { name: 'Terms of Service', path: '/terms' },
    { name: 'Privacy Policy', path: '/privacy' }
  ];

  return (
    <footer className='bg-gray-900 text-gray-300'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12'>
        <div className='grid grid-cols-1 md:grid-cols-4 gap-8'>
          {/* Brand */}
          <div className='col-span-1 md:col-span-1'>
            <div className='flex items-center space-x-3 mb-4'>
              <img 
                src='https://customer-assets.emergentagent.com/job_smart-money-route/artifacts/6teoyzj7_Generated%20Image%20November%2018%2C%202025%20-%203_06PM.png' 
                alt='RouteFlow AI' 
                className='h-10 w-auto'
              />
              <span className='text-xl font-bold text-white'>RouteFlow AI</span>
            </div>
            <p className='text-sm text-gray-400 mb-4'>
              AI-powered international money transfers. Fast, secure, and affordable.
            </p>
            
            {/* Social Media Icons */}
            <div className='flex space-x-4'>
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className={`text-gray-400 ${social.color} transition-colors`}
                    aria-label={social.name}
                  >
                    <Icon className='w-5 h-5' />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className='text-white font-semibold mb-4'>Quick Links</h3>
            <ul className='space-y-2'>
              {quickLinks.map((link) => (
                <li key={link.name}>
                  {link.isHash ? (
                    <button 
                      onClick={() => handleHashLink(link.path)}
                      className='text-gray-400 hover:text-emerald-400 transition-colors text-sm text-left'
                    >
                      {link.name}
                    </button>
                  ) : (
                    <a 
                      href={link.path}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(link.path);
                      }}
                      className='text-gray-400 hover:text-emerald-400 transition-colors text-sm cursor-pointer'
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Coverage Info - replacing Supported Routes */}
          <div>
            <h3 className='text-white font-semibold mb-4'>Coverage Info</h3>
            <ul className='space-y-2'>
              <li>
                <Link 
                  to='/supported-countries' 
                  className='text-gray-400 hover:text-emerald-400 transition-colors text-sm flex items-center gap-2'
                >
                  🌍 Supported Countries (121)
                </Link>
              </li>
              <li>
                <Link 
                  to='/payment-methods' 
                  className='text-gray-400 hover:text-emerald-400 transition-colors text-sm flex items-center gap-2'
                >
                  💳 Payment Methods (10)
                </Link>
              </li>
              <li>
                <Link 
                  to='/fiat-currencies' 
                  className='text-gray-400 hover:text-emerald-400 transition-colors text-sm flex items-center gap-2'
                >
                  💱 Fiat Currencies (44)
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className='text-white font-semibold mb-4'>Stay Updated</h3>
            <p className='text-sm text-gray-400 mb-4'>
              Subscribe for updates and special offers
            </p>
            <div className='flex'>
              <input
                type='email'
                placeholder='Your email'
                className='flex-1 px-3 py-2 bg-gray-800 text-white rounded-l-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm'
              />
              <button className='px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-r-lg transition-colors'>
                <Mail className='w-4 h-4' />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className='border-t border-gray-800 mt-8 pt-8'>
          {/* Technology Provider Disclaimer - Option A */}
          <div className='bg-gray-800/50 rounded-lg p-4 mb-6'>
            <div className='flex items-center justify-center gap-2 text-center'>
              <svg className="w-5 h-5 text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <p className='text-sm text-gray-400'>
                <span className='text-emerald-400 font-medium'>RouteFlow AI is a technology provider only.</span> All financial services are provided by licensed partners. We do not hold, custody, or control user funds.
              </p>
            </div>
          </div>
          
          <div className='flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0'>
            <div className='text-sm text-gray-400'>
              © {currentYear} RouteFlow AI, LLC. All rights reserved.
            </div>
            <div className='flex space-x-6'>
              {legalLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className='text-sm text-gray-400 hover:text-emerald-400 transition-colors'
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
